import apiClient from '@/services/api'
import { INITIAL_PAYROLL_PAY_CODE_MAPPINGS, PAYROLL_MAPPING_KEYS } from './constants'

const resolveEntity = (data, possibleKeys = []) => {
  for (const key of possibleKeys) {
    if (data?.[key] && typeof data[key] === 'object') {
      return data[key]
    }
  }

  return {}
}

const resolvePayCodeMappings = data => {
  const source =
    data?.payroll_settings?.pay_codes_mapping ??
    resolveEntity(data, ['mapping', 'pay_code_mapping', 'pay_code_mappings']) ??
    {}

  return PAYROLL_MAPPING_KEYS.reduce((acc, key) => {
    const valueFromSource = source?.[key]

    acc[key] = valueFromSource ?? null
    return acc
  }, {})
}

export default {
  /**
   * Fetches payrolls for the current project and commits the data to the store.
   * It handles pagination with default values for `page` and `size`.
   * If the request fails or the server returns an error, an exception is thrown.
   * The loading state is managed using the `setLoadingPayrolls` mutation.
   *
   * @param {Object} context - Vuex context object containing commit and rootState.
   * @param {Function} context.commit - Vuex commit function to call mutations.
   * @param {Object} context.rootState - Vuex root state, used to get the current project ID.
   * @param {Object} payload - Payload containing pagination parameters.
   * @param {Number} [payload.page=1] - The page number for pagination (default: 1).
   * @param {Number} [payload.size=10] - The number of items per page (default: 10).
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async fetchPayrolls({ commit, rootState }, { page = 1, size = 10 }) {
    try {
      const { data } = await apiClient.post('/api/payroll/get_all', {
        project_id: rootState.applicationState.currentProject.id,
        page,
        size
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setPayrolls', data)
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches a single payroll by ID and commits the data to the store.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing the payroll ID.
   * @param {String} payload.payroll_id - The ID of the payroll to fetch.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async fetchPayroll({ commit }, { payroll_id }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/get`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setActivePayroll', data.payroll)
    } catch (error) {
      throw error.message
    }
  },

  async deletePayroll({ commit, state }, { payroll_id }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/delete`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}.`)
      }

      commit('setActivePayroll', null)

      if (state.payrolls) {
        const updatedItems = (state.payrolls?.items ?? []).filter(item => item.id !== payroll_id)

        commit('setPayrolls', {
          ...state.payrolls,
          total_count: state.payrolls.total_count - 1,
          items: updatedItems
        })
      }
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches all pay periods for the current project and commits the data to the store.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async fetchPayPeriods({ commit, rootState }) {
    try {
      const { data } = await apiClient.post('/api/payroll/pay_period/get_all', {
        project_id: rootState.applicationState.currentProject.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setPayPeriods', data.pay_periods)
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Creates a new payroll for the current project and commits the data to the store.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing payroll details.
   * @param {String} payload.pay_period_start - The start date of the pay period.
   * @param {String} payload.pay_period_end - The end date of the pay period.
   * @param {String} payload.preparing_end - The preparation end date.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async createPayroll({ commit, rootState }, { pay_period_start, pay_period_end, preparing_end }) {
    try {
      const { data } = await apiClient.post('/api/payroll/create', {
        project_id: rootState.applicationState.currentProject.id,
        pay_period_start,
        pay_period_end,
        preparing_end
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setActivePayroll', data.payroll)

      return data.payroll.id
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches metrics for a specific payroll by ID and commits the data to the store.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing the payroll ID.
   * @param {String} payload.payroll_id - The ID of the payroll for which metrics are to be fetched.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async fetchMetrics({ commit }, { payroll_id }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/metrics/get`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setMetrics', data.metrics)
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches adjustments for a specific payroll by ID and commits the data to the store.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing the payroll ID.
   * @param {String} payload.payroll_id - The ID of the payroll for which adjustments are to be fetched.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async fetchAdjustments({ commit }, { payroll_id }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/adjustment/get_all`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setAdjustments', data.adjustments)
    } catch (error) {
      throw error.message
    }
  },

  setAdjustmentsShiftsLoading({ commit }, { adjustment_id, loading }) {
    commit('SET_ADJUSTMENTS_SHIFTS_LOADING', {
      adjustment_id,
      loading
    })
  },
  /**
   * Fetches all adjustment shifts for a specific adjustment and payroll ID and commits the data to the store.
   * It handles pagination with default values for `page` and `size`.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing adjustment and payroll IDs and pagination parameters.
   * @param {String} payload.adjustment_id - The ID of the adjustment for which shifts are to be fetched.
   * @param {String} payload.payroll_id - The ID of the payroll related to the adjustment.
   * @param {Number} [payload.page=1] - The page number for pagination (default: 1).
   * @param {Number} [payload.size=10] - The number of items per page (default: 10).
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async fetchAdjustmentShifts({ commit }, { adjustment_id, payroll_id, page = 1, size = 50 }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/adjustment/${adjustment_id}/shift/get_all`, {
        page,
        size
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return { data, adjustment_id }
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches estimated shifts for a specific payroll ID and commits the data to the store.
   * It handles pagination with default values for `page`, `size`, and applies a default filter.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing payroll ID, pagination parameters, and filter.
   * @param {String} payload.payroll_id - The ID of the payroll for which estimated shifts are to be fetched.
   * @param {Number} [payload.page=1] - The page number for pagination (default: 1).
   * @param {Number} [payload.size=10] - The number of items per page (default: 10).
   * @param {Object} [payload.filter={ type: "ready_to_pay" }] - Filter for fetching shifts (default filter: { type: "ready_to_pay" }).
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async fetchEstimatedShifts({ commit }, { payroll_id, page = 1, size = 50, filter = { type: 'ready_to_pay' } }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/shift/estimated/get_all`, {
        page,
        size,
        filter
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setShifts', {
        type: filter.type,
        shifts: data
      })
    } catch (error) {
      throw error.message
    }
  },

  async fetchShifts({ commit }, { payroll_id, page = 1, size = 10, filter = { type: 'ready_to_pay' } }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/shift/get_all`, {
        page,
        size,
        filter
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setShifts', {
        type: filter.type,
        shifts: data
      })
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches all payment requests for the active payroll and commits the data to the store.
   * It handles pagination with default values for `page` and `size`.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing pagination parameters and filter.
   * @param {Number} [payload.page=1] - The page number for pagination (default: 1).
   * @param {Number} [payload.size=10] - The number of items per page (default: 10).
   * @param {Object} [payload.filter={}] - Optional filter for fetching payment requests.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async fetchPaymentRequests({ state, commit }, { page = 1, size = 10, filter = {} }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${state.activePayroll.id}/payment_request/get_all`, {
        page,
        size
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setPaymentRequests', data)
    } catch (error) {
      throw error.message
    }
  },

  async fetchCalculations({ commit }, { payroll_id }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/calculations/get`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the calculations.'}`)
      }

      commit('setCalculations', data.calculation)
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches all signed associates for a specific payroll ID and commits the data to the store.
   * It handles pagination with default values for `page` and `size`.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing payroll ID and pagination parameters.
   * @param {String} payload.payroll_id - The ID of the payroll for which signed associates are to be fetched.
   * @param {Number} [payload.page=1] - The page number for pagination (default: 1).
   * @param {Number} [payload.size=10] - The number of items per page (default: 10).
   * @param {Object} [payload.filter={}] - Optional filter for fetching signed associates.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async fetchSignedAssociates({ commit }, { payroll_id, page = 1, size = 50, filter = {} }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/associate/signed/get_all`, {
        page,
        size
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setSignedAssociates', {
        items: data.associates,
        total_count: data.associates.length
      })
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches all unsigned associates for a specific payroll ID and commits the data to the store.
   * It handles pagination with default values for `page` and `size`.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing payroll ID and pagination parameters.
   * @param {String} payload.payroll_id - The ID of the payroll for which unsigned associates are to be fetched.
   * @param {Number} [payload.page=1] - The page number for pagination (default: 1).
   * @param {Number} [payload.size=10] - The number of items per page (default: 10).
   * @param {Object} [payload.filter={}] - Optional filter for fetching unsigned associates.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async fetchUnsignedAssociates({ commit }, { payroll_id, page = 1, size = 50, filter = {} }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/associate/not_signed/get_all`, {
        page,
        size
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setUnsignedAssociates', {
        items: data.associates,
        total_count: data.associates.length
      })
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Fetches associates that require validation for a specific payroll ID and commits the data to the store.
   * It handles pagination with default values for `page` and `size`, and applies a filter for statuses.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing payroll ID and pagination parameters.
   * @param {String} payload.payroll_id - The ID of the payroll for which validation associates are to be fetched.
   * @param {Number} [payload.page=1] - The page number for pagination (default: 1).
   * @param {Number} [payload.size=10] - The number of items per page (default: 10).
   * @param {Object} [payload.filter={}] - Optional filter for fetching validation associates.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async fetchValidationAssociates({ commit }, { payroll_id, page = 1, size = 50, filter = {} }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/associate/validation/get_all`, {
        page,
        size,
        filter: {
          statuses: ['initial', 'started']
        }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setValidationAssociates', data)
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Starts the validation process for a specific associate in a payroll.
   * It updates the validation status of the associate in the state and fetches metrics after starting validation.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing payroll and associate IDs.
   * @param {String} payload.payroll_id - The ID of the payroll in which validation starts.
   * @param {String} payload.associate_id - The ID of the associate being validated.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async startValidate({ commit, state, dispatch }, { payroll_id, associate_id, all = false }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/associate/shift/validate/start`, {
        associate_id,
        all
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      if (state.validationAssociates.items) {
        const updatedItems = state.validationAssociates.items.map(item =>
          associate_id.includes(item.id) ? { ...item, payroll_validation_status: 'started' } : item
        )

        commit('setValidationAssociates', {
          ...state.validationAssociates,
          items: updatedItems,
          total_count: updatedItems.length
        })
      }

      await dispatch('fetchMetrics', { payroll_id })

      return data.result
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Completes the validation process for a specific associate in a payroll.
   * It updates the state by removing the associate from validation associates and adding them to unsigned associates.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing payroll and associate IDs.
   * @param {String} payload.payroll_id - The ID of the payroll in which validation is completed.
   * @param {String} payload.associate_id - The ID of the associate being validated.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async completeValidate({ commit, state, dispatch }, { payroll_id, associate_id, all = false }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/associate/shift/validate/complete`, {
        associate_id,
        all
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      if (state.validationAssociates.items) {
        const associates = state.validationAssociates.items
          .filter(item => associate_id.includes(item.id))
          .map(item => ({ ...item, payroll_validation_status: 'validated' }))

        if (associates.length) {
          commit('setUnsignedAssociates', {
            items: [...state.unsignedAssociates.items, ...associates],
            total_count: state.unsignedAssociates.total_count + associates.length
          })
        }
      }

      await dispatch('fetchMetrics', { payroll_id })

      return data.result
    } catch (error) {
      throw error.message
    }
  },

  async cancelValidate({ commit, state, dispatch }, { payroll_id, associate_id, all = false }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/associate/shift/validate/cancel`, {
        associate_id,
        all
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      if (state.validationAssociates.items) {
        const updatedItems = state.validationAssociates.items.map(item =>
          associate_id.includes(item.id) ? { ...item, payroll_validation_status: 'initial' } : item
        )

        commit('setValidationAssociates', {
          ...state.validationAssociates,
          items: updatedItems,
          total_count: updatedItems.length
        })
      }

      await dispatch('fetchMetrics', { payroll_id })

      return data.result
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Signs a payroll for a specific associate by an admin.
   * It updates the state by moving the associate from unsigned associates to signed associates.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing payroll and associate IDs.
   * @param {String} payload.payroll_id - The ID of the payroll being signed.
   * @param {String} payload.signature_id - The ID of the signature used for signing.
   * @param {String | Array} payload.associate_id - The ID or IDs of the associate(s) being signed.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async signPayrollByAdmin({ commit, state, dispatch }, { payroll_id, signature_id, associate_id, all = false }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/associate/by_admin/sign`, {
        signature_id,
        associate_id,
        all
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      if (state.unsignedAssociates.items) {
        const associates = state.unsignedAssociates.items
          .filter(item => associate_id.includes(item.id))
          .map(item => ({
            ...item,
            payroll_validation_status: 'admin_signed'
          }))

        if (associates.length) {
          commit('setSignedAssociates', {
            items: [...state.signedAssociates.items, ...associates],
            total_count: state.signedAssociates.total_count + associates.length
          })
        }

        if (associate_id.length === state.unsignedAssociates.total_count || all) {
          await dispatch('fetchPayroll', { payroll_id })
        }
      }

      await dispatch('fetchMetrics', { payroll_id })

      return data.result
    } catch (error) {
      throw error.message
    }
  },

  /**
   * Completes a payroll process by signing it with the provided signature.
   * It updates the state to set the active payroll to null once the payroll is completed.
   * If the request fails or the server returns an error, an exception is thrown.
   *
   * @param {Object} payload - Payload containing payroll and signature IDs.
   * @param {String} payload.payroll_id - The ID of the payroll being completed.
   * @param {String} payload.signature_id - The ID of the signature used for completing the payroll.
   *
   * @throws {Error} Throws an error if the request fails or the server returns a non-zero error code.
   */
  async completePayroll({ state, commit }, { payroll_id, signature_id }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/complete`, {
        signature_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setActivePayroll', {
        ...state.activePayroll,
        status: 'paid'
      })
    } catch (error) {
      throw error.message
    }
  },

  async fetchAssociatesShifts({ commit }, { payroll_id, associate_id, page = 1, size = 10, filters = {} }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/associate/shift/estimated/get_all`, {
        page,
        size,
        associate_id,
        filter: filters
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setAssociateShifts', data)

      return data
    } catch (error) {
      throw error.message
    }
  },

  async fetchAssociatesPayments({ commit }, { payroll_id, associate_id, page = 1, size = 10, filter = {} }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/associate/payment_request/get_all`, {
        page,
        size,
        associate_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? "An error occurred while loading the associate's payments."}`)
      }

      commit('setAssociatePayments', data)
    } catch (error) {
      throw error.message
    }
  },

  async getAssociatesCalculation({ commit }, { payroll_id, associate_id }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/associate/calculations/get`, {
        associate_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? "An error occurred while loading the associate's calculations."}`)
      }

      commit('setAssociateCalculations', data.calculation)
    } catch (error) {
      throw error.message
    }
  },

  async downloadReportTimeAttendance(_, { payroll_id, format, filters = {} }) {
    try {
      const { data } = await apiClient.get(`/api/report/payroll/${payroll_id}/time_attendance/${format}/download`, {
        params: filters
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.url
    } catch (error) {
      throw error.message
    }
  },

  async downloadReportShiftTimeCards(_, { payroll_id, filters = {} }) {
    try {
      const { data } = await apiClient.get(`/api/report/payroll/${payroll_id}/shift_card/download`, { params: filters })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.url
    } catch (error) {
      throw error.message
    }
  },

  async downloadReportExternalSystem(_, { payroll_id, filters }) {
    try {
      const { data } = await apiClient.get(`/api/report/payroll/${payroll_id}/external_system/download`, {
        params: filters
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.url ?? data.result
    } catch (error) {
      throw error.message
    }
  },

  async downloadReportLoanRequest(_, { payroll_id }) {
    try {
      const { data } = await apiClient.get(`/api/report/payroll/${payroll_id}/loan_request/download`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.url
    } catch (error) {
      throw error.message
    }
  },

  async sendReportTimeAttendance(_, { payroll_id, format, email, filters = {} }) {
    try {
      const { data } = await apiClient.get(
        `/api/report/payroll/${payroll_id}/time_attendance/${format}/send_email/${email}`,
        { params: filters }
      )

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async sendReportShiftTimeCards(_, { payroll_id, email, filters = {} }) {
    try {
      const { data } = await apiClient.get(`/api/report/payroll/${payroll_id}/shift_card/send_email/${email}`, {
        params: filters
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async sendReportExternalSystem(_, { payroll_id, email, filters }) {
    try {
      const { data } = await apiClient.get(`/api/report/payroll/${payroll_id}/external_system/send_email/${email}`, {
        params: filters
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async sendReportISolvedAssociateOverview(_, { payroll_id }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/report/isolved/associate_overview/send`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async recalculatePayrollPremiumPay(_, { payroll_id }) {
    try {
      const data = await apiClient.post(`/api/payroll/${payroll_id}/recalculate`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async recalculatePayrollPremiumPaySettings({ rootState }) {
    try {
      const data = await apiClient.post('/api/payroll-settings/recalculate', {
        project_id: rootState.applicationState.currentProject.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async sendReportLoanRequest(_, { payroll_id, email }) {
    try {
      const { data } = await apiClient.get(`/api/report/payroll/${payroll_id}/loan_request/send_email/${email}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async exportAssociateCalculations(_, { payroll_id, associate_id, export_method, email }) {
    try {
      const { data } = await apiClient.post(`/api/payroll/${payroll_id}/associate/${associate_id}/calculation/export`, {
        export_method,
        email
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.url
    } catch (error) {
      throw error.message
    }
  },

  async fetchPayrollScopePresets({ commit, rootState }) {
    try {
      const { data } = await apiClient.post('/api/payroll-settings/scope/get_all', {
        project_id: rootState.applicationState.currentProject.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Failed to fetch scopes.'}`)
      }

      commit('setScopePresets', data.scopes)
      return data.scopes
    } catch (error) {
      throw error.message
    }
  },

  async createPayrollScopePreset({ rootState, state, commit }, { scope }) {
    try {
      const { data } = await apiClient.post('/api/payroll-settings/scope/create', {
        ...scope,
        project_id: rootState.applicationState.currentProject.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Failed to create scope.'}`)
      }

      commit('setScopePresets', [...state.scopePresets, data.scope])
    } catch (error) {
      throw error.message
    }
  },

  async updatePayrollScopePreset({ rootState, state, commit }, { scope }) {
    try {
      const { data } = await apiClient.post('/api/payroll-settings/scope/update', {
        ...scope,
        project_id: rootState.applicationState.currentProject.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Failed to update scope.'}`)
      }

      commit(
        'setScopePresets',
        state.scopePresets.map(item =>
          item.id === scope.id ? { ...data.scope, is_global: state.scopeGlobal.id === scope.id } : { ...item }
        )
      )
    } catch (error) {
      throw error.message
    }
  },

  async deletePayrollScopePreset({ rootState, state, commit }, { scope_id }) {
    try {
      const { data } = await apiClient.post('/api/payroll-settings/scope/delete', {
        project_id: rootState.applicationState.currentProject.id,
        scope_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Failed to delete scope.'}`)
      }

      commit(
        'setScopePresets',
        state.scopePresets.filter(item => item.id !== scope_id)
      )
    } catch (error) {
      throw error.message
    }
  },

  async fetchPayrollPremiumPayRules({ commit, rootState }) {
    try {
      const { data } = await apiClient.post('/api/payroll-settings/premium-pay/get_all', {
        project_id: rootState.applicationState.currentProject.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Failed to fetch premium pay rules.'}`)
      }

      commit('setPremiumPayRules', data.premium_pays)
      return data.premium_pays
    } catch (error) {
      throw error.message
    }
  },

  async fetchPayrollPremiumPayRule({ rootState }, { premium_pay_id }) {
    try {
      const { data } = await apiClient.post('/api/payroll-settings/premium-pay/get', {
        project_id: rootState.applicationState.currentProject.id,
        premium_pay_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Failed to fetch premium pay rule.'}`)
      }

      return data.premium_pay
    } catch (error) {
      throw error.message
    }
  },

  async savePayrollPremiumPayRule({ rootState, dispatch, commit, state }, { premiumPayRule }) {
    try {
      const premiumPayId = premiumPayRule?.premium_pay_id ?? premiumPayRule?.id
      const endpoint = premiumPayId
        ? '/api/payroll-settings/premium-pay/update'
        : '/api/payroll-settings/premium-pay/create'

      const payload = {
        ...premiumPayRule,
        project_id: rootState.applicationState.currentProject.id
      }

      if (premiumPayId) {
        payload.premium_pay_id = premiumPayId
      } else {
        delete payload.premium_pay_id
      }

      delete payload.id
      delete payload.scope

      const { data } = await apiClient.post(endpoint, payload)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Failed to save premium pay rule.'}`)
      }

      commit(
        'setPremiumPayRules',
        premiumPayId
          ? state.premiumPayRules.map(item => (item.id === premiumPayId ? { ...data.premium_pay } : { ...item }))
          : [...state.premiumPayRules, data.premium_pay]
      )

      return data.premium_pay
    } catch (error) {
      throw error.message
    }
  },

  async updatePayrollPremiumPayEnabled({ dispatch }, { premiumPayRule, is_enabled }) {
    const payload = {
      ...premiumPayRule,
      scope_id: premiumPayRule?.scope_id ?? premiumPayRule?.scope?.id,
      is_enabled
    }

    await dispatch('savePayrollPremiumPayRule', {
      premiumPayRule: payload
    })
  },

  async deletePayrollPremiumPayRule({ rootState }, { premium_pay_id }) {
    try {
      const { data } = await apiClient.post('/api/payroll-settings/premium-pay/delete', {
        project_id: rootState.applicationState.currentProject.id,
        premium_pay_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Failed to delete premium pay rule.'}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async fetchPayrollSettings({ commit, rootState }) {
    try {
      const { data } = await apiClient.post('/api/payroll-settings/get', {
        project_id: rootState.applicationState.currentProject.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Failed to fetch payroll pay code mappings.'}`)
      }

      commit('setPayCodeMappings', {
        ...INITIAL_PAYROLL_PAY_CODE_MAPPINGS,
        ...data.payroll_settings.pay_codes_mapping
      })
      commit('setPremiumPayRules', data.payroll_settings.premium_pays)
      commit(
        'setScopePresets',
        data.payroll_settings.scopes.map(item => ({ ...item, is_global: data.payroll_settings.scope.id === item.id }))
      )
      commit('setScopeGlobal', data.payroll_settings.scope)
    } catch (error) {
      throw error.message
    }
  },

  async updatePayrollPayCodeMappings({ commit, rootState }, { mappings }) {
    try {
      const { data } = await apiClient.post('/api/payroll-settings/update', {
        project_id: rootState.applicationState.currentProject.id,
        pay_codes_mapping: mappings
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Failed to update payroll pay code mappings.'}`)
      }

      const nextMappings = {
        ...INITIAL_PAYROLL_PAY_CODE_MAPPINGS,
        ...mappings,
        ...resolvePayCodeMappings(data)
      }

      commit('setPayCodeMappings', nextMappings)
      return nextMappings
    } catch (error) {
      throw error.message
    }
  },

  async updatePayrollAttachments({ commit, state }, { file_ids, id }) {
    try {
      const { data } = await apiClient.put(`/api/payroll/${id}/files`, {
        file_ids
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg} `)
      }

      const updatedPayroll = {
        ...state.activePayroll,
        files: data.files
      }

      commit('setActivePayroll', updatedPayroll)
    } catch (error) {
      throw error.message
    }
  }
}
